import { ControlsLayer } from "@manager/layer/ControlsLayer";
import { PosterLayer } from "@manager/layer/PosterLayer";
import { CTALayerObserver } from "@manager/layerObserver/CTALayerObserver";
import { ResourcesCache } from "@manager/loader/scenarioLoader/ResourcesCache";
import { inject, injectable } from "inversify";
import { CTA } from "../../../model/playerConfiguration/CTA";
import { ScenarioGlobalState } from "../../../model/scenarioGlobalState/ScenarioGlobalState";
import { ScenarioPath } from "../../../model/scenarioPath/ScenarioPath";
import { TYPES } from "../../di/types";
import { CTALayer } from "../../layer/CTALayer";
import { CachedCTAResource } from "../../loader/scenarioLoader/CachedCTAResource";
import { ScenarioTimeHelper } from "../util/ScenarioTimeHelper";

export interface CTALayerManager {
  newScenarioElement(scenarioGlobalState: ScenarioGlobalState): void;
  resourcesLoaded(resourcesCache: ResourcesCache, totalTime: number): void;
  setTotalTime(totalTime: number): void;
  moveTo(time: number): void;
  performAction(ctaId: number, actionId: number, ...args: any[]): void;
  registerObserver(observer: CTALayerObserver): void;
  displayAllCTAsForCTAOnlyElemnt(): void;
}

@injectable()
export class CTALayerManagerImpl implements CTALayerManager {
  private observer: CTALayerObserver;
  private reusableCTAs: number[];
  private currentCTAs: CTA[] = [];
  private scenarioPath: ScenarioPath;
  private ctasByType: Map<string, CachedCTAResource>;
  private totalTime: number;

  constructor(
    @inject(TYPES.layer.CTALayer) private layer: CTALayer,
    @inject(TYPES.layer.PosterLayer) private posterLayer: PosterLayer,
    @inject(TYPES.layer.ControlsLayer) private controlsLayer: ControlsLayer,
  ) {}

  newScenarioElement(scenarioGlobalState: ScenarioGlobalState): void {
    this.scenarioPath = scenarioGlobalState.scenarioPath;
    this.layer.setAspectRatio(scenarioGlobalState.aspectRatio);
  }

  resourcesLoaded(resourcesCache: ResourcesCache, totalTime: number): void {
    this.ctasByType = resourcesCache.ctasByType;
    this.totalTime = totalTime;

    this.intializeReusableCTAs();
    this.removeCTAs(this.currentCTAs);
  }

  setTotalTime(totalTime: number): void {
    this.totalTime = totalTime;
  }

  registerObserver(observer: CTALayerObserver): void {
    this.observer = observer;
  }

  moveTo(time: number): void {
    const ctas = this.findCTAsAtTime(time);
    this.displayCTAs(ctas);
    this.controlsLayer.setIsPlaybackBlockingCTADisplayed(ctas.length > 0);
  }

  performAction(ctaId: number, actionId: number, ...args: any[]): void {
    this.layer.performAction(ctaId, actionId, args);
  }

  displayAllCTAsForCTAOnlyElemnt(): void {
    const ctas = this.scenarioPath.currentElement.ctas;
    const playbackStoppingCTAs = ctas.filter((cta) => cta.stopsPlayback);
    this.displayCTAs(ctas);

    this.controlsLayer.setIsPlaybackBlockingCTADisplayed(playbackStoppingCTAs.length > 0);
  }

  private intializeReusableCTAs() {
    if (!this.reusableCTAs) {
      const hashToCTACount = new Map<number, number>();
      for (const scElem of this.scenarioPath.allElements) {
        for (const cta of scElem.ctas) {
          const countForHash = hashToCTACount.get(cta.hashCode) || 0;
          hashToCTACount.set(cta.hashCode, countForHash + 1);
        }
      }

      const duplicatedCTAHashes = Array.from(hashToCTACount.entries())
        .filter((entry) => entry[1] > 1)
        .map((entry) => entry[0]);

      this.reusableCTAs = duplicatedCTAHashes;
    }
  }

  private displayCTAs(ctas: CTA[]): void {
    const ctaHashesToDisplay = ctas.map((cta) => cta.hashCode);

    const ctasToRemove = this.currentCTAs.filter((cta) => ctaHashesToDisplay.indexOf(cta.hashCode) < 0);
    const ctasToLoad = ctas.filter((cta) => !this.currentCTAs.find((currCTA) => currCTA.id === cta.id));

    this.removeCTAs(ctasToRemove);
    this.loadCTAs(ctasToLoad);
  }

  private removeCTAs(ctasToRemove: CTA[]): void {
    for (const ctaToRemove of ctasToRemove) {
      this.currentCTAs = this.currentCTAs.filter((cta) => !ctasToRemove.find((toRemove) => toRemove.id === cta.id));
      this.layer.removeCTA(ctaToRemove.id, ctaToRemove.hashCode);
    }

    const ctaWithPoster = ctasToRemove.find((cta) => !!cta.poster);
    if (ctaWithPoster) {
      this.posterLayer.hidePoster();
    }
  }

  private loadCTAs(ctas: CTA[]): void {
    for (const cta of ctas) {
      const cachedCTAResource = this.ctasByType.get(cta.type);
      if (cachedCTAResource) {
        this.currentCTAs.push(cta);
        const isCTAReusable = this.reusableCTAs.indexOf(cta.hashCode) > -1;
        this.layer.loadCTA(cta.hashCode, isCTAReusable, cta.id, cachedCTAResource, cta.customAttributes);

        this.observer.onCTAShow(cta);
      }
    }

    const ctaWithPoster = ctas.find((cta) => !!cta.poster);
    const posterToShow = ctaWithPoster && ctaWithPoster.poster;
    if (posterToShow) {
      this.posterLayer.showPoster(posterToShow);
    }
  }

  private findCTAsAtTime(time: number): CTA[] {
    const ctasAtTime = this.scenarioPath.currentElement.ctas
      .filter((cta) => cta.triggerType === "RANGE")
      .filter(
        (cta) =>
          ScenarioTimeHelper.getTimeFromVideoStart(this.totalTime, cta.visibilityRange.startTime) <= time &&
          (cta.visibilityRange.endTime === "END" ||
            ScenarioTimeHelper.getTimeFromVideoStart(this.totalTime, cta.visibilityRange.endTime) >= time),
      );

    if (time >= this.totalTime) {
      const ctasAtEnd = this.scenarioPath.currentElement.ctas.filter((cta) => cta.triggerType === "END");
      return ctasAtTime.concat(ctasAtEnd);
    }
    return ctasAtTime;
  }
}
